import React, { useContext, useState } from "react";
import { IonCard, IonCardContent, IonCardHeader, IonCardTitle } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { subDays, format } from "date-fns";
import BackendFactory from "../API/BackendFactory";
import { LunchListObject } from "../API/lib/JsonUtils";
import TemplateCard from "./TemplateCard";
import Debouncer from "./DebouncerUtility";
import getFlavoriaLang from "../utils/flavoriaLang";
import GlobalContext from "../GlobalContext";
import { logout_sessionexpired } from "../logout";
import { rootPath } from "../Utils";

const debouncer = new Debouncer();
let cachedLang = "";

interface Props {
    language: string;
}

const LunchCards: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [errorCode, setErrorCode] = useState("");
    const [lunchJson, setLunchJson] = useState("");
    const globalContext = useContext(GlobalContext);

    const now = new Date();
    const currentHour = now.getHours();
    const todayStr = format(now, "yyyy-MM-dd");

    const day = ["fri", "mon", "tue", "wed", "thu", "fri", "fri"];
    const currentDay = day[now.getDay()];

    const getDateByWeekday = (weekday: string) => {
        const day_lookup = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
        let day = now.getDay();
        if (day == 0) day = 7;
        const delta = day_lookup.indexOf(weekday) - day;
        return format(subDays(now, -delta), "yyyy-MM-dd");
    };

    let lunchDateStr: string;
    let useTomorrowTitle = false;

    if (currentHour >= 16) {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowDay = tomorrow.getDay();

        if (tomorrowDay >= 1 && tomorrowDay <= 5) {
            lunchDateStr = format(tomorrow, "yyyy-MM-dd");
            useTomorrowTitle = true;
        } else {
            return null;
        }
    } else {
        lunchDateStr = getDateByWeekday(currentDay);
    }

    debouncer.set_debouncer(() => {
        const lang = getFlavoriaLang(props.language);
        cachedLang = lang;
        BackendFactory.getBackend()
            .getLunchList(lunchDateStr)
            .then((result) => {
                setLunchJson(result);
                setLoading(false);
            })
            .catch((reason) => {
                if (reason == "Expired Token") {
                    logout_sessionexpired(globalContext, t);
                } else {
                    setErrorCode(typeof reason == "string" ? reason : "UnexpectedError");
                    setLoading(false);
                }
            });
    });

    if (!loading && lunchJson != "" && getFlavoriaLang(props.language) != cachedLang) {
        setLoading(true);
    }

    if (loading) {
        debouncer.call();
    }

    if (loading) {
        return <TemplateCard />;
    }

    const isToday = lunchDateStr === todayStr;
    const shouldShowData = isToday || useTomorrowTitle;

    const lang = getFlavoriaLang(props.language);
    let titles: string[] = [];
    if (errorCode === "" && lunchJson !== "" && shouldShowData) {
        try {
            const ll = new LunchListObject(lunchJson);
            titles = ll.getLunchItems(lang).map((item) => item.title);
        } catch (_e) {
            // lunch list not available
        }
    }

    if (titles.length === 0) {
        return null;
    }

    return (
        <IonCard onClick={() => history.push(`${rootPath}/lunchlist`)} style={{ cursor: "pointer" }}>
            <IonCardHeader>
                <IonCardTitle>{useTomorrowTitle ? t("Tomorrow's Lunch List") : t("Lunch List")}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
                {titles.map((title, idx) => (
                    <div
                        key={idx}
                        style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            padding: "4px 0",
                        }}
                    >
                        {title}
                    </div>
                ))}
            </IonCardContent>
        </IonCard>
    );
};

export default LunchCards;
